/*
  Store locator (sections/store-locator.liquid).
  Progressive enhancement over the server-rendered list: adds the Leaflet map, text search,
  province and Open now filters, distance sorting from a typed place or the browser's
  location, and the Open now badge computed in each store's time zone.

  No API keys. Tiles come from Esri or OpenStreetMap; place lookup uses Nominatim
  (OpenStreetMap's geocoder) at most once per second and caches results in localStorage.
  Leaflet is loaded from jsdelivr before this file.
*/
(function () {
  'use strict';

  /* Esri's grey canvas basemaps need no key and match the site's paper-and-ink palette.
     CARTO's basemaps started requiring an API key in 2026, so they are not used. */
  var ESRI_ATTRIBUTION = 'Tiles &copy; <a href="https://www.esri.com/" target="_blank" rel="noopener">Esri</a> &mdash; Esri, HERE, Garmin, &copy; OpenStreetMap contributors';
  var TILES = {
    light: {
      url: 'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}',
      attribution: ESRI_ATTRIBUTION,
      maxZoom: 18,
      maxNativeZoom: 16
    },
    dark: {
      url: 'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}',
      attribution: ESRI_ATTRIBUTION,
      maxZoom: 18,
      maxNativeZoom: 16
    },
    osm: {
      url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a> contributors',
      maxZoom: 19
    }
  };
  var DAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  var GEOCODE_URL = 'https://nominatim.openstreetmap.org/search';
  var CACHE_KEY = 'uc-store-locator-geocode-v1';
  var PIN_SVG =
    '<svg viewBox="0 0 30 40" aria-hidden="true" focusable="false">' +
    '<path class="sl-pin__fill" d="M15 0C6.7 0 0 6.6 0 14.8 0 25.5 15 40 15 40s15-14.5 15-25.2C30 6.6 23.3 0 15 0z"/>' +
    '<circle cx="15" cy="14.5" r="5.5" fill="#fff"/></svg>';

  /* ---------- small helpers ---------- */

  function el(tag, attrs, children) {
    var node = document.createElement(tag);
    if (attrs) Object.keys(attrs).forEach(function (k) { node.setAttribute(k, attrs[k]); });
    (children || []).forEach(function (c) {
      node.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
    });
    return node;
  }

  function normalize(s) {
    return String(s || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  function haversine(a, b) {
    var R = 6371;
    var dLat = (b.lat - a.lat) * Math.PI / 180;
    var dLng = (b.lng - a.lng) * Math.PI / 180;
    var s = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(a.lat * Math.PI / 180) * Math.cos(b.lat * Math.PI / 180) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return 2 * R * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
  }

  function formatDistance(km) {
    if (km < 1) return Math.round(km * 1000) + ' m away';
    return (km < 10 ? km.toFixed(1) : String(Math.round(km))) + ' km away';
  }

  function delay(ms) {
    return new Promise(function (r) { setTimeout(r, ms); });
  }

  /* ---------- opening hours ---------- */

  function dayIndex(token) {
    var t = String(token || '').toLowerCase().replace(/[^a-z]/g, '');
    if (t.length < 3) return -1;
    for (var i = 0; i < 7; i++) if (DAYS[i].indexOf(t.slice(0, 3)) === 0) return i;
    return -1;
  }

  function meridiem(s) {
    if (!s) return '';
    return /p/i.test(s) ? 'pm' : 'am';
  }

  function toMinutes(h, m, mer) {
    h = h % 12;
    if (mer === 'pm') h += 12;
    return h * 60 + m;
  }

  function formatTime(minutes) {
    minutes = ((minutes % 1440) + 1440) % 1440;
    var h = Math.floor(minutes / 60);
    var m = minutes % 60;
    var suffix = h >= 12 ? 'PM' : 'AM';
    var h12 = h % 12 === 0 ? 12 : h % 12;
    return h12 + (m ? ':' + (m < 10 ? '0' + m : m) : '') + ' ' + suffix;
  }

  /* "10:00 AM – 9:30 PM" -> { open, close, text }; "Closed" -> null; unreadable -> undefined */
  function parseRange(s) {
    s = String(s || '').trim();
    if (!s) return undefined;
    if (/closed/i.test(s)) return null;
    var m = s.match(/(\d{1,2})(?:[:.](\d{2}))?\s*(a\.?m\.?|p\.?m\.?)?\s*(?:-|–|—|to)\s*(\d{1,2})(?:[:.](\d{2}))?\s*(a\.?m\.?|p\.?m\.?)?/i);
    if (!m) return undefined;
    var h1 = parseInt(m[1], 10), m1 = parseInt(m[2] || '0', 10), p1 = meridiem(m[3]);
    var h2 = parseInt(m[4], 10), m2 = parseInt(m[5] || '0', 10), p2 = meridiem(m[6]);
    if (!p1 && !p2) { p1 = h1 >= 7 && h1 <= 11 ? 'am' : 'pm'; p2 = h2 >= 7 && h2 <= 11 && h2 > h1 ? 'am' : 'pm'; }
    if (!p1) p1 = h1 >= 7 && h1 <= 11 ? 'am' : (h1 === 12 ? 'pm' : (h1 < h2 ? p2 : 'am'));
    if (!p2) p2 = h2 >= 7 && h2 <= 11 ? 'am' : 'pm';
    var open = toMinutes(h1, m1, p1);
    var close = toMinutes(h2, m2, p2);
    if (close <= open) close += 1440;
    return { open: open, close: close, text: s };
  }

  /* Multi-line text, one day per line, into a 7-slot table indexed Sunday = 0. */
  function parseHours(text) {
    var table = [undefined, undefined, undefined, undefined, undefined, undefined, undefined];
    String(text || '').split(/\r?\n/).forEach(function (line) {
      var m = line.match(/^\s*([a-z]+\.?)\s*(?:(-|–|—|to|&|and|,)\s*([a-z]+\.?))?\s*[:\-–]?\s*(.*)$/i);
      if (!m) return;
      var a = dayIndex(m[1]);
      if (a < 0) return;
      var b = m[3] ? dayIndex(m[3]) : -1;
      var days = [a];
      if (b >= 0) {
        if (/^(-|–|—|to)$/i.test(m[2])) {
          days = [];
          var d = a;
          for (var guard = 0; guard < 7; guard++) { days.push(d); if (d === b) break; d = (d + 1) % 7; }
        } else {
          days = [a, b];
        }
      }
      var range = parseRange(m[4]);
      days.forEach(function (idx) { table[idx] = range; });
    });
    return table;
  }

  function nowIn(tz) {
    try {
      var parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: tz, weekday: 'long', hour: 'numeric', minute: 'numeric', hour12: false
      }).formatToParts(new Date());
      var o = {};
      parts.forEach(function (p) { o[p.type] = p.value; });
      var day = dayIndex(o.weekday);
      if (day < 0) throw new Error('weekday');
      return { day: day, minutes: (parseInt(o.hour, 10) % 24) * 60 + parseInt(o.minute, 10) };
    } catch (e) {
      var d = new Date();
      return { day: d.getDay(), minutes: d.getHours() * 60 + d.getMinutes() };
    }
  }

  /* -> { state: 'open' | 'closed' | 'unknown', badge, today } */
  function openState(table, tz) {
    var now = nowIn(tz);
    var today = table[now.day];
    var yesterday = table[(now.day + 6) % 7];
    var result = { state: 'unknown', badge: '', today: '' };

    if (yesterday && yesterday.close > 1440 && now.minutes + 1440 < yesterday.close) {
      return { state: 'open', badge: 'Open · until ' + formatTime(yesterday.close), today: today ? today.text : '' };
    }
    if (today === undefined) return result;
    if (today === null) return { state: 'closed', badge: 'Closed today', today: 'Closed' };

    result.today = today.text;
    if (now.minutes >= today.open && now.minutes < today.close) {
      result.state = 'open';
      result.badge = 'Open · until ' + formatTime(today.close);
    } else if (now.minutes < today.open) {
      result.state = 'closed';
      result.badge = 'Closed · opens ' + formatTime(today.open);
    } else {
      result.state = 'closed';
      result.badge = 'Closed now';
    }
    return result;
  }

  /* ---------- geocoding (Nominatim, throttled, cached) ---------- */

  var geocodeQueue = Promise.resolve();

  function readCache() {
    try { return JSON.parse(window.localStorage.getItem(CACHE_KEY) || '{}') || {}; } catch (e) { return {}; }
  }
  function writeCache(cache) {
    try { window.localStorage.setItem(CACHE_KEY, JSON.stringify(cache)); } catch (e) { /* private mode, full, or blocked */ }
  }

  function geocode(query) {
    var key = normalize(query);
    if (!key) return Promise.reject(new Error('empty'));
    var cache = readCache();
    if (cache[key] && cache[key].lat) return Promise.resolve(cache[key]);

    var run = function (q) {
      var url = GEOCODE_URL + '?format=jsonv2&limit=1&countrycodes=ca&accept-language=en&q=' + encodeURIComponent(q);
      return fetch(url, { headers: { Accept: 'application/json' } }).then(function (r) {
        if (!r.ok) throw new Error('geocode ' + r.status);
        return r.json();
      });
    };

    geocodeQueue = geocodeQueue.then(function () { return delay(1100); });
    var job = geocodeQueue.then(function () {
      return run(query).then(function (rows) {
        if ((!rows || !rows.length) && /^[a-z]\d[a-z]\s?\d[a-z]\d$/i.test(query.trim())) {
          /* Full postal codes are patchy in OpenStreetMap; the first three characters usually resolve. */
          return delay(1100).then(function () { return run(query.trim().slice(0, 3)); });
        }
        return rows;
      });
    }).then(function (rows) {
      if (!rows || !rows.length) throw new Error('not-found');
      var hit = { lat: parseFloat(rows[0].lat), lng: parseFloat(rows[0].lon), label: String(rows[0].display_name || query).split(',').slice(0, 2).join(',').trim() };
      var c = readCache();
      c[key] = hit;
      writeCache(c);
      return hit;
    });
    return job;
  }

  /* ---------- the locator ---------- */

  /* Analytics. Every interaction is published as the Shopify customer event "store_locator:action"
     (a custom pixel in Settings › Customer events forwards it to Google Analytics), and also sent
     through gtag() when a tag runs on the page itself, or pushed to a GTM dataLayer if one exists. */
  function track(action, store, extra) {
    var payload = { action: action, page: window.location.pathname };
    if (store) { payload.store_id = store.id; payload.store_name = store.name; payload.province = store.province || ''; }
    if (extra) { for (var k in extra) { if (Object.prototype.hasOwnProperty.call(extra, k)) payload[k] = extra[k]; } }
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish('store_locator:action', payload);
      }
    } catch (e) { /* analytics must never break the page */ }
    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'store_locator_action', payload);
      } else if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        var dl = { event: 'store_locator_action' };
        for (var j in payload) { if (Object.prototype.hasOwnProperty.call(payload, j)) dl[j] = payload[j]; }
        window.dataLayer.push(dl);
      }
    } catch (e2) { /* same */ }
  }

  function StoreLocator(section) {
    this.section = section;
    section.__storeLocator = this;
    this.mapEl = section.querySelector('[data-sl-map]');
    this.listEl = section.querySelector('[data-sl-list]');
    this.input = section.querySelector('[data-sl-input]');
    this.form = section.querySelector('[data-sl-form]');
    this.submit = section.querySelector('[data-sl-submit]');
    this.submitLabel = section.querySelector('[data-sl-submit-label]');
    this.near = section.querySelector('[data-sl-near]');
    this.statusEl = section.querySelector('[data-sl-status]');
    this.countEl = section.querySelector('[data-sl-count]');
    this.emptyEl = section.querySelector('[data-sl-empty]');
    this.openNow = section.querySelector('[data-sl-open-now]');
    this.chips = Array.prototype.slice.call(section.querySelectorAll('[data-sl-province]'));

    var raw = section.querySelector('[data-sl-data]');
    try { this.data = JSON.parse(raw ? raw.textContent : '[]'); } catch (e) { this.data = []; }

    this.stores = [];
    this.byId = {};
    var self = this;
    this.data.forEach(function (d) {
      var card = section.querySelector('[data-sl-card][data-id="' + d.id + '"]');
      if (!card) return;
      var store = {
        id: d.id,
        name: d.name,
        status: d.status,
        address: d.address,
        geocodeQuery: d.geocodeQuery,
        lat: typeof d.lat === 'number' ? d.lat : null,
        lng: typeof d.lng === 'number' ? d.lng : null,
        hours: d.hours ? parseHours(d.hours) : null,
        timezone: d.timezone || 'America/Toronto',
        search: normalize([d.name, d.city, d.province, d.address, d.postalCode, (d.postalCode || '').replace(/\s+/g, '')].join(' ')),
        province: card.getAttribute('data-province') || '',
        card: card,
        marker: null,
        distance: null,
        visible: true,
        open: 'unknown'
      };
      self.stores.push(store);
      self.byId[store.id] = store;
    });
    this.originalOrder = this.stores.slice();
    this.province = '';
    this.origin = null;
    this.youMarker = null;
    this.activeId = null;

    this.initMap();
    this.bind();
    this.updateOpenBadges();
    this.resolveMissingCoordinates();
    this.applyFilters();

    var minute = 60 * 1000;
    this.badgeTimer = window.setInterval(function () { self.updateOpenBadges(); self.applyFilters(true); }, minute);
  }

  StoreLocator.prototype.initMap = function () {
    if (!this.mapEl || typeof window.L === 'undefined') return;
    var style = this.section.getAttribute('data-map-style') || 'light';
    var tiles = TILES[style] || TILES.light;
    if (style === 'custom') {
      tiles = {
        url: this.section.getAttribute('data-tile-url'),
        attribution: this.section.getAttribute('data-tile-attribution') || '',
        maxZoom: 19
      };
    }
    this.zoomSingle = parseInt(this.section.getAttribute('data-zoom') || '13', 10);

    this.map = L.map(this.mapEl, {
      scrollWheelZoom: false,
      zoomControl: true,
      attributionControl: true,
      worldCopyJump: true
    });
    L.tileLayer(tiles.url, {
      attribution: tiles.attribution,
      subdomains: tiles.subdomains || 'abc',
      maxZoom: tiles.maxZoom || 19,
      maxNativeZoom: tiles.maxNativeZoom || tiles.maxZoom || 19
    }).addTo(this.map);
    this.map.setView([45.5, -76.5], 6);

    /* Wheel zoom only once the map has been clicked or focused, so page scrolling is not hijacked. */
    var self = this;
    this.map.on('focus click', function () { self.map.scrollWheelZoom.enable(); });
    this.map.on('blur mouseout', function () { self.map.scrollWheelZoom.disable(); });

    this.stores.forEach(function (s) { self.addMarker(s); });
  };

  StoreLocator.prototype.addMarker = function (store) {
    if (!this.map || store.lat === null || store.lng === null || store.marker) return;
    var self = this;
    var marker = L.marker([store.lat, store.lng], {
      icon: L.divIcon({ className: 'sl-pin', html: PIN_SVG, iconSize: [30, 40], iconAnchor: [15, 40], popupAnchor: [0, -38] }),
      title: store.name,
      alt: store.name,
      keyboard: true,
      riseOnHover: true
    });
    var directions = store.card.querySelector('.sl__card-actions a');
    var popup = el('div', null, [
      el('strong', null, [store.name]),
      store.address,
      el('br'),
      directions ? el('a', { href: directions.getAttribute('href'), target: '_blank', rel: 'noopener' }, ['Directions']) : ''
    ]);
    marker.bindPopup(popup, { maxWidth: 260, closeButton: true });
    marker.on('click', function () { self.setActive(store.id, false); track('map_pin', store); });
    marker.on('popupclose', function () { if (self.activeId === store.id) self.setActive(null, false); });
    store.marker = marker;
    if (store.visible) marker.addTo(this.map);
  };

  StoreLocator.prototype.resolveMissingCoordinates = function () {
    var self = this;
    var missing = this.stores.filter(function (s) { return s.lat === null || s.lng === null; });
    if (!missing.length) return;
    missing.forEach(function (s) {
      geocode(s.geocodeQuery).then(function (hit) {
        s.lat = hit.lat;
        s.lng = hit.lng;
        self.addMarker(s);
        if (self.origin) self.sortByDistance(self.origin, self.originLabel, true);
        self.fitVisible();
      }).catch(function () { /* stays off the map but on the list */ });
    });
  };

  StoreLocator.prototype.bind = function () {
    var self = this;
    if (this.form) {
      this.form.addEventListener('submit', function (e) { e.preventDefault(); self.search(); });
    }
    /* Clicks on anything marked data-sl-track, attributed to the store card they sit in. */
    this.section.addEventListener('click', function (e) {
      var el = e.target && e.target.closest ? e.target.closest('[data-sl-track]') : null;
      if (!el) return;
      var card = el.closest('[data-sl-card]');
      track(el.getAttribute('data-sl-track'), card ? self.byId[card.getAttribute('data-id')] : null);
    });
    Array.prototype.forEach.call(this.section.querySelectorAll('[data-sl-hours]'), function (d) {
      d.addEventListener('toggle', function () {
        if (!d.open) return;
        var card = d.closest('[data-sl-card]');
        track('hours', card ? self.byId[card.getAttribute('data-id')] : null);
      });
    });
    if (this.input) {
      var t;
      this.input.addEventListener('input', function () {
        window.clearTimeout(t);
        t = window.setTimeout(function () { self.applyFilters(); }, 120);
      });
    }
    if (this.near) this.near.addEventListener('click', function () { self.locate(); });
    if (this.openNow) this.openNow.addEventListener('change', function () {
      self.applyFilters();
      track('filter', null, { province: self.province || 'all', open_now: !!self.openNow.checked });
    });
    this.chips.forEach(function (chip) {
      chip.addEventListener('click', function () {
        self.province = chip.getAttribute('data-sl-province') || '';
        self.chips.forEach(function (c) { c.setAttribute('aria-pressed', c === chip ? 'true' : 'false'); });
        self.applyFilters();
        track('filter', null, { province: self.province || 'all', open_now: !!(self.openNow && self.openNow.checked) });
      });
    });
    var reset = this.section.querySelector('[data-sl-reset]');
    if (reset) reset.addEventListener('click', function () { self.reset(); });

    this.stores.forEach(function (s) {
      var btn = s.card.querySelector('[data-sl-focus]');
      if (btn) btn.addEventListener('click', function () { self.setActive(s.id, true); });
    });
  };

  StoreLocator.prototype.setStatus = function (message, tone) {
    if (!this.statusEl) return;
    this.statusEl.textContent = message || '';
    if (tone) this.statusEl.setAttribute('data-tone', tone); else this.statusEl.removeAttribute('data-tone');
  };

  StoreLocator.prototype.setBusy = function (busy, label) {
    if (this.submit) this.submit.setAttribute('aria-busy', busy ? 'true' : 'false');
    if (this.submitLabel) this.submitLabel.textContent = label || 'Search';
    if (this.near) this.near.disabled = !!busy;
  };

  StoreLocator.prototype.updateOpenBadges = function () {
    var now = nowIn('America/Toronto');
    this.stores.forEach(function (s) {
      var badge = s.card.querySelector('[data-sl-open-badge]');
      var today = s.card.querySelector('[data-sl-today]');
      if (!s.hours || s.status === 'Coming soon' || s.status === 'Temporarily closed') {
        s.open = 'unknown';
        if (badge) badge.hidden = true;
        return;
      }
      var state = openState(s.hours, s.timezone);
      s.open = state.state;
      if (badge) {
        if (state.state === 'unknown') {
          badge.hidden = true;
        } else {
          badge.hidden = false;
          badge.textContent = state.badge;
          badge.setAttribute('data-state', state.state);
        }
      }
      if (today) today.textContent = state.today ? 'Today: ' + state.today : '';
      var local = nowIn(s.timezone);
      var rows = s.card.querySelectorAll('tr[data-sl-day]');
      Array.prototype.forEach.call(rows, function (row) {
        var label = row.getAttribute('data-sl-day') || '';
        var m = label.match(/^\s*([a-z]+)\s*(?:(-|–|—|to|&|and|,)\s*([a-z]+))?/i);
        var hit = false;
        if (m) {
          var a = dayIndex(m[1]);
          var b = m[3] ? dayIndex(m[3]) : -1;
          if (b >= 0 && /^(-|–|—|to)$/i.test(m[2])) {
            var d = a;
            for (var g = 0; g < 7; g++) { if (d === local.day) hit = true; if (d === b) break; d = (d + 1) % 7; }
          } else {
            hit = a === local.day || b === local.day;
          }
        }
        row.classList.toggle('is-today', hit);
      });
      void now;
    });
  };

  StoreLocator.prototype.applyFilters = function (quiet) {
    var q = normalize(this.input ? this.input.value : '');
    var openOnly = !!(this.openNow && this.openNow.checked);
    var province = this.province;
    var shown = 0;
    var self = this;

    this.stores.forEach(function (s) {
      var ok = true;
      if (q && s.search.indexOf(q) === -1) {
        /* allow every word to match somewhere: "eaton toronto" */
        ok = q.split(' ').every(function (w) { return s.search.indexOf(w) !== -1; });
      }
      if (ok && province && s.province !== province) ok = false;
      if (ok && openOnly && s.open !== 'open') ok = false;
      s.visible = ok;
      s.card.hidden = !ok;
      if (ok) shown++;
      if (s.marker && self.map) {
        if (ok && !self.map.hasLayer(s.marker)) s.marker.addTo(self.map);
        if (!ok && self.map.hasLayer(s.marker)) self.map.removeLayer(s.marker);
      }
    });

    if (this.countEl) {
      var total = this.stores.length;
      this.countEl.textContent = shown === total
        ? total + (total === 1 ? ' store' : ' stores')
        : shown + ' of ' + total + ' stores';
    }
    if (this.emptyEl) this.emptyEl.hidden = shown !== 0 || this.stores.length === 0;
    if (this.activeId && this.byId[this.activeId] && !this.byId[this.activeId].visible) this.setActive(null, false);
    if (!quiet) this.fitVisible();
  };

  StoreLocator.prototype.fitVisible = function () {
    if (!this.map) return;
    var pts = this.stores.filter(function (s) { return s.visible && s.lat !== null; }).map(function (s) { return [s.lat, s.lng]; });
    if (this.origin) pts.push([this.origin.lat, this.origin.lng]);
    if (!pts.length) return;
    if (pts.length === 1) { this.map.setView(pts[0], this.zoomSingle); return; }
    if (this.origin) {
      /* Zoom to the searched place and its three nearest stores, not the whole country. */
      var near = this.stores.filter(function (s) { return s.visible && s.lat !== null && s.distance !== null; })
        .slice(0, 3).map(function (s) { return [s.lat, s.lng]; });
      near.push([this.origin.lat, this.origin.lng]);
      this.map.fitBounds(near, { padding: [48, 48], maxZoom: 12 });
      return;
    }
    this.map.fitBounds(pts, { padding: [40, 40], maxZoom: 12 });
  };

  StoreLocator.prototype.setActive = function (id, pan) {
    var self = this;
    this.activeId = id;
    this.stores.forEach(function (s) {
      var on = s.id === id;
      s.card.classList.toggle('is-active', on);
      if (s.marker) {
        var node = s.marker.getElement();
        if (node) node.classList.toggle('is-active', on);
        if (on) s.marker.setZIndexOffset(1000); else s.marker.setZIndexOffset(0);
      }
    });
    var store = id ? this.byId[id] : null;
    if (!store || !this.map) return;
    if (store.marker) {
      if (pan) {
        this.map.flyTo([store.lat, store.lng], Math.max(this.map.getZoom(), this.zoomSingle), { duration: 0.6 });
        store.marker.openPopup();
      }
    } else if (pan) {
      this.setStatus(store.name + ' is not on the map yet.', null);
    }
    if (pan && window.innerWidth < 990) {
      this.mapEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else if (!pan) {
      store.card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
    void self;
  };

  StoreLocator.prototype.sortByDistance = function (origin, label, quiet) {
    var self = this;
    this.origin = origin;
    this.originLabel = label;
    this.stores.forEach(function (s) {
      s.distance = s.lat === null ? null : haversine(origin, { lat: s.lat, lng: s.lng });
      var out = s.card.querySelector('[data-sl-distance]');
      if (out) {
        if (s.distance === null) { out.hidden = true; } else { out.hidden = false; out.textContent = formatDistance(s.distance); }
      }
    });
    this.stores.sort(function (a, b) {
      if (a.distance === null && b.distance === null) return 0;
      if (a.distance === null) return 1;
      if (b.distance === null) return -1;
      return a.distance - b.distance;
    });
    this.stores.forEach(function (s) { self.listEl.appendChild(s.card); });

    if (this.map) {
      if (!this.youMarker) {
        this.youMarker = L.marker([origin.lat, origin.lng], {
          icon: L.divIcon({ className: 'sl-you', html: '<span></span>', iconSize: [16, 16], iconAnchor: [8, 8] }),
          title: label || 'Your location',
          alt: label || 'Your location',
          interactive: false,
          zIndexOffset: 500
        }).addTo(this.map);
      } else {
        this.youMarker.setLatLng([origin.lat, origin.lng]);
      }
    }
    if (!quiet) this.applyFilters();
  };

  StoreLocator.prototype.search = function () {
    var raw = this.input ? this.input.value.trim() : '';
    var self = this;
    if (!raw) { this.reset(); return; }

    /* The input filter is debounced; run it now so the count is current when Enter beats the timer. */
    this.applyFilters();
    var matches = this.stores.filter(function (s) { return s.visible; });
    if (matches.length && normalize(raw) && this.stores.some(function (s) { return s.search.indexOf(normalize(raw)) !== -1; })) {
      this.setStatus(matches.length + (matches.length === 1 ? ' store matches ' : ' stores match ') + '"' + raw + '".', null);
      track('search', null, { query_type: 'name', term: raw, results: matches.length });
      return;
    }

    /* Nothing matches by name, so treat it as a place and sort by distance from there. */
    this.setBusy(true, 'Searching…');
    this.setStatus('Looking up "' + raw + '"…', null);
    geocode(raw).then(function (hit) {
      self.setBusy(false);
      if (self.input) self.input.value = '';
      self.sortByDistance(hit, hit.label);
      self.setStatus('Stores nearest to ' + hit.label + ', closest first.', null);
      track('search', null, { query_type: 'place', term: raw, place: hit.label, results: self.stores.length });
    }).catch(function (err) {
      self.setBusy(false);
      track('search', null, { query_type: 'place', term: raw, results: 0, error: err && err.message === 'not-found' ? 'not_found' : 'lookup_failed' });
      self.setStatus(err && err.message === 'not-found'
        ? 'We could not find "' + raw + '". Try a city or a postal code.'
        : 'The place lookup is not responding. Try again in a moment, or pick a store from the list.', 'error');
    });
  };

  StoreLocator.prototype.locate = function () {
    var self = this;
    if (!navigator.geolocation) { this.setStatus('Your browser does not share location. Type a city or postal code instead.', 'error'); return; }
    this.setBusy(true, 'Locating…');
    this.setStatus('Finding your location…', null);
    navigator.geolocation.getCurrentPosition(function (pos) {
      self.setBusy(false);
      if (self.input) self.input.value = '';
      self.sortByDistance({ lat: pos.coords.latitude, lng: pos.coords.longitude }, 'Your location');
      self.setStatus('Stores nearest to you, closest first.', null);
      track('locate', null, { ok: true });
    }, function () {
      self.setBusy(false);
      track('locate', null, { ok: false });
      self.setStatus('We could not get your location. Type a city or postal code instead.', 'error');
    }, { enableHighAccuracy: false, timeout: 10000, maximumAge: 5 * 60 * 1000 });
  };

  StoreLocator.prototype.reset = function () {
    var self = this;
    if (this.input) this.input.value = '';
    if (this.openNow) this.openNow.checked = false;
    this.province = '';
    this.chips.forEach(function (c) { c.setAttribute('aria-pressed', (c.getAttribute('data-sl-province') || '') === '' ? 'true' : 'false'); });
    this.origin = null;
    this.originLabel = null;
    if (this.youMarker && this.map) { this.map.removeLayer(this.youMarker); this.youMarker = null; }
    this.stores = this.originalOrder.slice();
    this.stores.forEach(function (s) {
      s.distance = null;
      var out = s.card.querySelector('[data-sl-distance]');
      if (out) out.hidden = true;
      self.listEl.appendChild(s.card);
    });
    this.setStatus('', null);
    this.applyFilters();
    if (this.input) this.input.focus();
  };

  StoreLocator.prototype.destroy = function () {
    window.clearInterval(this.badgeTimer);
    if (this.map) this.map.remove();
    this.section.__storeLocator = null;
  };

  /* ---------- boot ---------- */

  var instances = {};

  function init(root) {
    Array.prototype.forEach.call((root || document).querySelectorAll('[data-store-locator]'), function (section) {
      var id = section.getAttribute('data-section-id') || 'default';
      /* The element remembers its instance, so a second copy of this script (theme editor, app reloads) does not re-initialise the map container. */
      var previous = section.__storeLocator || instances[id];
      if (previous) previous.destroy();
      instances[id] = new StoreLocator(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  /* Theme editor re-renders the section without reloading scripts. */
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector('[data-store-locator]')) init(e.target);
  });
})();
